import {
	getLocalStorageItemAsString,
	setLocalStorageItem,
} from '@atlassian/search-common/local-storage';

/**
 * LocalStorage shape:
 *
 *   {
 *     "<userId or 'anonymous-user'>": {
 *       "permanent": ["<keyword>", ...],   // never show again for these keywords
 *       "lastDismissedAt": <epoch-ms>     // most-recent dismissal time, used to snooze all other keywords for 24h
 *     }
 *   }
 *
 * Two-tier dismissal model:
 *   1. The dismissed keyword goes into `permanent` — it is suppressed forever
 *      for this user. ("Never show again for 'PR' if I dismissed when typing 'PR'.")
 *   2. The dismissal also bumps `lastDismissedAt` — all OTHER matching
 *      keywords are suppressed for 24h after the most-recent dismissal.
 *      ("Don't show this banner for any keyword for 1 day after I dismiss it,
 *      but next day try other keywords again.")
 *
 * NOTE: the legacy schema stored a flat keyword → timestamp map and snoozed
 * the dismissed keyword for 24h only. That contradicted the permanent-per-
 * keyword rule and is intentionally not migrated forward — old entries are
 * read but produce neither permanent nor recent-snooze entries, so users see
 * the banner again on a fresh keyword. That is acceptable because the legacy
 * release was storybook-only.
 */

export const GITHUB_TIP_DISMISS_KEY: string = 'atlassian.unified-search.github-tip-dismiss';
export const ANONYMOUS_USER_ID: string = 'anonymous-user';
export const GITHUB_TIP_SNOOZE_MS: number = 24 * 60 * 60 * 1000; // 1 day

type UserDismissState = {
	permanent?: string[];
	lastDismissedAt?: number;
};

type GithubTipDismissStore = {
	[userId: string]: UserDismissState;
};

const readStore = (): GithubTipDismissStore => {
	const raw = getLocalStorageItemAsString(GITHUB_TIP_DISMISS_KEY);
	if (!raw) {
		return {};
	}
	try {
		const parsed = JSON.parse(raw);
		if (typeof parsed !== 'object' || parsed === null) {
			return {};
		}
		// Defensive: filter out any legacy-schema (flat keyword → number) entries
		// so they don't crash the typed reads below. Anything that doesn't look
		// like a UserDismissState is treated as missing.
		const out: GithubTipDismissStore = {};
		for (const [userKey, value] of Object.entries(parsed as Record<string, unknown>)) {
			if (typeof value !== 'object' || value === null) {
				continue;
			}
			const v = value as Record<string, unknown>;
			const next: UserDismissState = {};
			if (Array.isArray(v.permanent) && v.permanent.every((k) => typeof k === 'string')) {
				next.permanent = v.permanent as string[];
			}
			if (typeof v.lastDismissedAt === 'number') {
				next.lastDismissedAt = v.lastDismissedAt;
			}
			if (next.permanent || typeof next.lastDismissedAt === 'number') {
				out[userKey] = next;
			}
		}
		return out;
	} catch {
		return {};
	}
};

const writeStore = (store: GithubTipDismissStore, logException?: (error: Error) => void): void => {
	try {
		setLocalStorageItem(GITHUB_TIP_DISMISS_KEY, JSON.stringify(store));
	} catch (error) {
		logException?.(error as Error);
	}
};

export const shouldShowGithubTipForKeyword = (
	keyword: string,
	userId: string | undefined,
	now: number = Date.now(),
): boolean => {
	const userKey = userId || ANONYMOUS_USER_ID;
	const state = readStore()[userKey];
	if (!state) {
		return true;
	}
	const normalized = keyword.toLowerCase();
	// Tier 1: permanent suppression for this exact keyword.
	if (state.permanent?.includes(normalized)) {
		return false;
	}
	// Tier 2: any other keyword is suppressed for 24h after the most-recent
	// dismissal across any keyword.
	if (
		typeof state.lastDismissedAt === 'number' &&
		now - state.lastDismissedAt < GITHUB_TIP_SNOOZE_MS
	) {
		return false;
	}
	return true;
};

export const dismissGithubTipForKeyword = (
	keyword: string,
	userId: string | undefined,
	logException?: (error: Error) => void,
	now: number = Date.now(),
): void => {
	const userKey = userId || ANONYMOUS_USER_ID;
	const store = readStore();
	const next: UserDismissState = { ...(store[userKey] ?? {}) };
	const normalized = keyword.toLowerCase();
	const permanent = new Set(next.permanent ?? []);
	permanent.add(normalized);
	next.permanent = Array.from(permanent);
	next.lastDismissedAt = now;
	store[userKey] = next;
	writeStore(store, logException);
};
